import React, { useEffect, useState } from 'react'
import { GAbutton, GAtrack } from '../../services/ga'
import { addToastMsg, getWalletTokenAmount } from '../../services/amm'
import { getCalcId, getSwapQuote, updateCalcId } from '../../services/quoter'
import ConnectWallet from '../ConnectWallet'
import SlippageIndicator from './SlippageIndicator'
import SwapCard from './SwapCard'
import SwapButton from './SwapButton'
import BoxHeaderNew, { DEFAULT_SLIPPAGE } from '../BoxHeader/BoxHeader'
import { useStoreActions, useStoreState } from '../../model/hooks.ts'
import { getToken } from '../../model/tokens/helper.ts'
import { calculateRatioFromTickForTokens } from '../../blockchainUtils/math.ts'
import { useWalletContext } from '../WalletContext.tsx'
import { getCurrentTick, swapV3 } from '../../services/amm_v3.ts'
import { useDebounce } from 'usehooks-ts'
import { getAllCLPools } from '../../model/clpool/helper'
import Spinner from '../Spinner.tsx'
import { GreenDotIcon } from '../../svgIcons/GreenDotIcon'
import { Network } from '../../services/token.service.ts'
import BigNumber from 'bignumber.js'
import { SwapQuote } from '../../atoms.ts'
import { HelptipIcon } from '../../svgIcons/HelptipIcon.tsx'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { XIcon } from '../../svgIcons/XIcon.tsx'
import BannerCoins from '../../images/bgd/banner_coins.png'
import * as Sentry from '@sentry/react'

function SwapPage() {
    const { walletAddress, networkName: network } = useWalletContext()
    let [searchParams] = useSearchParams()
    const [token1, setToken1] = useState('')
    const [lastQuote, setLastQuote] = useState(undefined as unknown as SwapQuote)
    const [token1Amount, setToken1Amount] = useState('')
    const [token2, setToken2] = useState('')
    const [token2Amount, setToken2Amount] = useState('')
    const [leadingToken, setLeadingToken] = useState(0) // 0 is no token, 1/2 is the last updated token
    const [recalc, setRecalc] = useState(false)
    const [isCalculatingPrice, setIsCalculatingPrice] = useState(false)
    const [swapInProgress, setSwapInProgress] = useState(false)
    const tokens = useStoreState((state) => (network ? state.tokens.tokens[network] : {}))
    const [swapHash, setSwapHash] = useState('')
    const [poolAddress, setPoolAddress] = useState('')
    const selectedPool = useStoreState((state) => state.clPools.getPoolByAddress(network!, poolAddress))
    const [tokenAddedToWallet, setTokenAddedToWallet] = useState(false)
    const loadTransaction = useStoreActions((actions) => actions.transaction.loadTransaction)
    const transaction = useStoreState((state) => state.transaction.getTransaction(swapHash))
    const loadBalances = useStoreActions((actions) => actions.tokens.loadBalances)
    const [currentRatio, setCurrentRatio] = useState(null as unknown as BigNumber)
    const debouncedToken1Amount = useDebounce(token1Amount, 1000)
    const debouncedToken2Amount = useDebounce(token2Amount, 1000)
    const [error, setError] = useState('')
    const [slippage, setSlippage] = useState(Number(DEFAULT_SLIPPAGE))
    const [quoterIntervalId, setQuoterIntervalId] = useState(undefined as unknown as NodeJS.Timeout)
    const [lastBlockId, setLastBlockId] = useState(undefined as unknown as number)
    const [isMigrateShow, setIsMigrateShow] = useState(true)
    const [isMaxClicked, setIsMaxClicked] = useState(false)
    const [rebalanceDismissed, setRebalanceDismissed] = useState(false)
    const [outOfRange, setOutOfRange] = useState(0)
    const navigate = useNavigate()
    useEffect(() => {
        if (network === 'mainnet-alpha') GAtrack('route_swap', 'route_swap', 'swap', -1)
        // loadCurrentTick() // return this??
    }, [])

    useEffect(() => {
        if (selectedPool) {
            loadCurrentTick(poolAddress)
        }
    }, [selectedPool])

    useEffect(() => {
        let tempToken1 = token1
        let tempToken2 = token2
        if (searchParams.get('from')) {
            setToken1(searchParams.get('from')!)
            if (tempToken2 === searchParams.get('from') && !searchParams.get('to')) setToken2('')
        }
        if (searchParams.get('to')) {
            setToken2(searchParams.get('to')!)
            if (tempToken1 === searchParams.get('to') && !searchParams.get('from')) setToken1('')
        }
    }, [searchParams, tokens])

    useEffect(() => {
        if (transaction && (transaction.status === 'ACCEPTED_ON_L2' || transaction.status === 'ACCEPTED_ON_L1')) {
            loadBalances({ network: network as Network, walletAddress: walletAddress! })
            setSwapHash('')
            setSwapInProgress(false)
            clearInterval(quoterIntervalId)
        }
        if (transaction && transaction.status === 'REVERTED') {
            setSwapHash('')
            setSwapInProgress(false)
            clearInterval(quoterIntervalId)
        }
    }, [transaction])

    useEffect(() => {
        if (tokens && Object.values(tokens).length > 0 && !searchParams.get('from') && !searchParams.get('to')) {
            Object.values(tokens).forEach((item) => {
                if (item && item.symbol === 'ETH') {
                    navigate(`/swap?from=${item.address}`)

                    getWalletTokenAmount(network as Network, item.address)
                }
            })
        }
    }, [tokens, network])

    useEffect(() => {
        if (recalc && ((debouncedToken1Amount && Number(debouncedToken1Amount) > 0) || (debouncedToken2Amount && Number(debouncedToken2Amount) > 0))) {
            setRecalc(false)
            if (quoterIntervalId) clearInterval(quoterIntervalId)
            setAmounts()
            const id = setInterval(() => {
                setAmounts(false)
            }, 20000)
            setQuoterIntervalId(id)
        }
        if (!debouncedToken1Amount && !debouncedToken2Amount) {
            clearInterval(quoterIntervalId)
        }
    }, [debouncedToken1Amount, debouncedToken2Amount, token1, token2])

    const handleToken1Select = (address: string) => {
        let url = `/swap?from=${address}`
        if (token2) url = url + `&to=${token2}`
        navigate(url)

        setError('')
        setRecalc(true)
        setToken1Amount('')
        GAbutton('swap-token1')
    }

    async function loadCurrentTick(poolAddress: string) {
        const tick = await getCurrentTick(network as Network, poolAddress)
        if (tick || tick == 0) {
            if (selectedPool) {
                const ratio = calculateRatioFromTickForTokens(tick, selectedPool.token1, selectedPool.token2)
                setCurrentRatio(ratio)
            }
        }
    }

    const handleToken2Select = (address: string) => {
        navigate(`/swap?from=${token1}&to=${address}`)
        setTokenAddedToWallet(address === token2)
        // setDisplayAddTokenToWallet(false)
        setError('')
        setToken2Amount('')
        setRecalc(true)
        GAbutton('swap-token2')
    }

    const handleTokenAmountChange = (token: string, amount: string) => {
        // fixme: consider limit decimals input according to token decimals
        const regex = /^(([0-9]+[.]?[0-9]*)|([.][0-9]+))$/
        setIsMaxClicked(false)
        if (token === token1 && (amount === '' || regex.test(amount))) {
            setToken2Amount('')
            setToken1Amount(amount)
            setLeadingToken(1)
            setRecalc(true)
        }
        if (token === token2 && (amount === '' || regex.test(amount))) {
            setToken1Amount('')
            setToken2Amount(amount)
            setLeadingToken(2)
            setRecalc(true)
        }
    }

    function onError(error: string) {
        if (error && error.includes('enough liquidity')) return setError('Not enough liquidity in pool')
        if (error && error.includes('no pools support this swap')) return setError('No pool for swap')
        if (error && error.includes('amount too low')) return setError('Amount Too Low')
        if (error && error.includes('Resource busy')) return setError('Network error')

        Sentry.captureException(`Error performing swap ${error}`)
        setError('Error performing swap')
    }

    async function setAmounts(shouldLoad = true) {
        if ((leadingToken === 1 && Number(token1Amount) === 0) || (leadingToken === 2 && Number(token2Amount) === 0)) return
        setError('')
        if (shouldLoad) {
            setIsCalculatingPrice(true)
        }
        try {
            if (token1 && token2) {
                const t1 = getToken(network as Network, token1)
                const t2 = getToken(network as Network, token2)
                if (t1 && t2) {
                    const pools = await getAllCLPools(network as Network, t1, t2)
                    const havePool = pools.length > 0
                    // follow the leader
                    if (leadingToken <= 1) {
                        if (havePool) {
                            updateCalcId()
                            const quote = await getSwapQuote(network as Network, t1, t2, token1Amount, true, slippage / 100, getCalcId())
                            setLastBlockId(quote!.blockNumber || 0)
                            setPoolAddress(quote!.pool.address || '')
                            if (quote === undefined || quote.err) {
                                setToken2Amount(0 as unknown as string)
                            } else {
                                setToken2Amount(quote.forwardSwap ? quote.token2Amount : quote.token1Amount)
                                setLastQuote(quote)
                            }
                        } else {
                            setToken2Amount(0 as unknown as string)
                        }
                    } else {
                        if (havePool) {
                            updateCalcId()
                            const quote = await getSwapQuote(network as Network, t1, t2, token2Amount, false, slippage / 100, getCalcId())
                            setPoolAddress(quote!.pool.address || '')

                            if (quote === undefined || quote.err) {
                                setToken1Amount(0 as unknown as string)
                            } else {
                                setToken1Amount(quote.forwardSwap ? quote.token1Amount : quote.token2Amount)
                                setLastQuote(quote)
                            }
                        } else {
                            setToken1Amount(0 as unknown as string)
                        }
                    }
                    setIsCalculatingPrice(false)
                    return
                }
            }
            // tokens not full
            // follow the leader
            if (leadingToken <= 1) {
                // cannot be 0, but just for safety
                setToken2Amount('')
                setIsCalculatingPrice(false)
                setLastQuote(undefined as unknown as SwapQuote)
            } else {
                setToken1Amount('')
                setIsCalculatingPrice(false)
                setLastQuote(undefined as unknown as SwapQuote)
            }
        } catch (e: any) {
            if (e.message && e.message.includes('Replacement calculation sent')) return
            onError(e.message)
            setIsCalculatingPrice(false)
            console.log('error setting amounts ', e)
        }
    }

    const handleSwap = async () => {
        if (lastQuote === undefined) {
            console.log('No quote to swap with')
            return
        }

        // if (token1 === ETH_TOKEN_ADDRESS && Number(token1Amount) > ETH_SWAP_MAX_ALLOWED) {
        //     addToastMsg(`ETH swap is limited to ${ETH_SWAP_MAX_ALLOWED} ETH`, 'error')
        //     GAbutton('swap-eth-over-max-allowed')
        //     return
        // }
        // if (token1 === ETH_TOKEN_ADDRESS && token2 === DAI_TOKEN_ADDRESS && Number(token1Amount) > ETH_TO_DAI_SWAP_MAX_ALLOWED) {
        //     addToastMsg(`ETH to DAI swap is limited to ${ETH_TO_DAI_SWAP_MAX_ALLOWED} ETH`, 'error')
        //     GAbutton('swap-eth-to-dai-over-max-allowed')
        //     return
        // }

        setSwapInProgress(true)
        GAbutton('swap')

        try {
            let label = 'swap'
            let token_obj = getToken(network as Network, token1)
            if (token_obj) label = 'swap from=' + token_obj.symbol + ':amount=' + token1Amount

            const tx = await swapV3(network as Network, lastQuote, slippage / 100, token1, token2)

            if (!tx) throw Error('Cannot perform transaction')
            if (network === 'mainnet-alpha') GAtrack('tx_initiated_swap', 'tx_initiated_swap', 'swap')
            if (network === 'mainnet-alpha') GAtrack(`tx_sent_swap`, 'tx_sent_swap', label)
            const timestampSent = new Date().getTime()

            loadTransaction({ txPayload: { [tx.hash]: { ...tx, labelFields: { timestampSent: timestampSent } } }, isGoogleLog: network === 'mainnet-alpha' })
            setSwapHash(tx.hash)
            setToken1Amount('')
            setToken2Amount('')
            setError('')
            if (!tokenAddedToWallet) {
                // setDisplayAddTokenToWallet(true)
                // setTimeout(() => {
                //     setDisplayAddTokenToWallet(false)
                // }, 15000)
            }
        } catch (e: any) {
            if (e.message.includes('User abort')) {
                addToastMsg('Swap Request: User Aborted', 'error')
            }
            setSwapInProgress(false)
        }
    }

    const switchTokensAndAmounts = () => {
        GAbutton('swap-switchtokens')
        // let tmpToken = token1
        let url = `/swap?to=${token1}`
        if (token2) url += `&from=${token2}`
        navigate(url)
        // if (token2) url = url + `&to=${token2}`

        // setToken1(token2)
        // setToken2(tmpToken)
        setToken1Amount(token2Amount)
        setToken2Amount(token1Amount)
        setLeadingToken(leadingToken === 1 ? 2 : 1)
        setTokenAddedToWallet(false)
        setLastQuote(undefined as unknown as SwapQuote)
        if (token2Amount && Number(token2Amount) > 0 && token1Amount && Number(token1Amount) > 0) {
            setIsCalculatingPrice(true)
            setRecalc(true)
        }
    }

    function renderRangeErrorMessage() {
        if (!error) return null
        return <div className='rounded-lg mt-4 p-4 text-[16px] font-bold bg-[#391F45] text-[#FF5353]'>{error}</div>
    }

    function renderSwapActionsCard() {
        if (isCalculatingPrice) return null
        if (error) return renderRangeErrorMessage()
        return (
            <div>
                <ConnectWallet secondary='true' />
                {/* {addTokenToWallet()} */}
                <SwapButton
                    network={network}
                    handleSwap={handleSwap}
                    activePoolId={-1}
                    token1={token1}
                    token1Amount={token1Amount}
                    token2={token2}
                    token2Amount={token2Amount}
                    swapInProgress={swapInProgress}
                />
            </div>
        )
    }

    function renderSwapHeader() {
        return <BoxHeaderNew slippage={slippage.toString()} setOutsideSlippage={(val) => setSlippage(val)} className='mb-4 lg:mb-6' name='Swap' />
    }

    function renderPriceCalculator() {
        if (!isCalculatingPrice) return null

        const placeholderText = 'Searching for the best price...'
        return (
            <div className='flex p-4 border border-[1px] border-[#0C3F6D] rounded-lg mt-2 text-[18px] text-[#8387A5]'>
                <Spinner />
                <span>{placeholderText}</span>
            </div>
        )
    }

    function renderLegacyPrompt() {
        if (!isMigrateShow) return null
        return (
            <div className='relative w-full h-[40px] rounded-[16px] overflow-hidden mb-[24px] mt-[24px]'>
                <XIcon
                    onClick={() => {
                        if (network === 'mainnet-alpha') GAtrack('hide_migrate_popup', 'hide_migrate_popup', 'hide_migrate_popup')
                        setIsMigrateShow(false)
                    }}
                    className='absolute right-[16px] top-[16px] w-[12px] h-[12px] text-[#68F2B0] z-[10] cursor-pointer'
                />
                <div className='absolute w-full h-full bg-[#68F2B0] opacity-[0.2]' />
                <div className='flex w-full p-4 h-full items-center absolute z-[4] text-[#68F2B0] text-[16px]'>
                    <HelptipIcon className='mr-2 w-[21px] h-[21px]' />
                    <div className='flex items-center text-[16px]'>
                        <span>You can migrate your Legacy Positions easily</span>
                        <Link
                            onClick={() => {
                                if (network === 'mainnet-alpha') GAtrack('click_migrate_popup', 'click_migrate_popup', 'click_migrate_popup')
                            }}
                            className='ml-1 underline'
                            to={'/positions?isLegacy=true'}>
                            now
                        </Link>
                    </div>
                </div>
            </div>
        )
    }

    function renderRebalancePrompt() {
        if (rebalanceDismissed) return null
        if (outOfRange == 0) return null
        const posText = outOfRange === 1 ? 'one position' : `${outOfRange} positions`
        return (
            <div className='relative w-full h-[60px] rounded-[16px] overflow-hidden mb-[24px] mt-[24px]'>
                <XIcon
                    onClick={() => {
                        if (network === 'mainnet-alpha') GAtrack('hide_rebalance_toast', 'hide_rebalance_toast', 'hide_rebalance_toast')
                        setRebalanceDismissed(true)
                    }}
                    className='absolute right-[16px] top-[16px] w-[12px] h-[12px] text-[#68F2B0] z-[10] cursor-pointer'
                />
                <div className='absolute w-full h-full bg-[#68F2B0] opacity-[0.2]' />
                <div className='flex w-full p-4 h-full items-center absolute z-[4] text-[#68F2B0] text-[16px]'>
                    <HelptipIcon className='mr-2 w-[21px] h-[21px]' />
                    <div className='flex flex-col text-[16px]'>
                        <span>You have {posText} out of range, not earning fees! </span>
                        <Link
                            onClick={() => {
                                if (network === 'mainnet-alpha') GAtrack('click_rebalance_toast', 'click_rebalance_toast', 'click_rebalance_toast')
                            }}
                            className='ml-1 underline'
                            to={'/positions'}>
                            Rebalance {outOfRange > 1 ? 'them' : 'it'} now with one click
                        </Link>
                    </div>
                </div>
            </div>
        )
    }
    // function renderMemePrompt() {
    //     if (UnknownTokenList.includes(token1.toLowerCase()) || UnknownTokenList.includes(token2.toLowerCase())) {
    //         return (
    //             <div className='relative w-full h-[82px] rounded-[16px] overflow-hidden mb-[24px] mt-[24px]'>
    //                 <div className='absolute w-full h-full bg-[#D86518] opacity-[0.2]' />
    //                 <div className='flex w-full p-4 h-full items-center absolute z-[4] text-[#D86518] text-[16px]'>
    //                     <HelptipIcon className='mr-2 w-[24px] h-[24px]' />
    //                     <div className='flex flex-col'>
    //                         <span>This token is new and unknown so please conduct your own research before trading.</span>
    //                     </div>
    //                 </div>
    //             </div>
    //         )
    //     }
    //     return null
    // }
    function renderIncentiveBanner() {
        if (network !== 'mainnet-alpha') return null

        return (
            <div className='relative flex bg-[#1D285A] h-[64px] lg:h-[100px] rounded-[16px] mt-[44px] mb-[24px] p-4 lg:p-6'>
                <div
                    style={{ background: `url(${BannerCoins})`, backgroundSize: 'cover', backgroundRepeat: 'no-repeat' }}
                    className='flex-shrink-0 relative bottom-[38px] lg:bottom-[57px] left-[-9px] lg:left-[-22px] w-[90px] h-[85px] lg:w-[154px] lg:h-[132px]'
                />
                <div className='h-full flex items-center w-full'>
                    <span className='max-w-[240px] w-[130px] lg:w-[auto] lg:text-[22px] text-[14px] font-bold lg:ml-[-12px]'>Starknet Incentive Program is Live!</span>
                    <div className='border-[2px] border-white rounded-[8px] bg-[#68ECF2] lg:px-4 px-2 py-1' style={{ boxShadow: '0px 0px 28.9px 0px #00F0FF' }}>
                        <span onClick={() => navigate('/rewards')} className='text-[12px] lg:text-[18px] cursor-pointer' style={{ color: 'rgba(12, 22, 71, 0.76)' }}>
                            Participate
                        </span>
                    </div>
                </div>
            </div>
        )
    }
    return (
        <div>
            {renderRebalancePrompt()}
            {renderIncentiveBanner()}
            {renderSwapHeader()}
            <SwapCard
                setIsMaxBalance={(isMax) => setIsMaxClicked(isMax)}
                isSelect2Disabled={isCalculatingPrice}
                network={network as Network}
                token2Amount={token2Amount}
                token2={token2}
                token1Amount={token1Amount}
                handleTokenAmountChange={handleTokenAmountChange}
                handleToken2Select={handleToken2Select}
                switchTokensAndAmounts={switchTokensAndAmounts}
                token1={token1}
                handleToken1Select={handleToken1Select}
            />
            {renderPriceCalculator()}
            {isCalculatingPrice ? null : (
                <SlippageIndicator
                    isMinSpent={leadingToken === 1}
                    slippage={slippage}
                    isMaxBalance={isMaxClicked}
                    forwardSwap={lastQuote?.forwardSwap}
                    currentRatio={currentRatio ? currentRatio.toNumber() : 0}
                    network={network as Network}
                    token1={token1}
                    token1Amount={token1Amount}
                    token2={token2}
                    token2Amount={token2Amount}
                    fee={lastQuote?.pool?.fee}
                    lastQuote={lastQuote}
                />
            )}
            {renderSwapActionsCard()}

            {lastBlockId && lastBlockId !== 0 ? (
                <div className='hidden lg:flex fixed bottom-[24px] right-[48px] items-center'>
                    <GreenDotIcon className='mr-2' />
                    <span>{lastBlockId}</span>
                </div>
            ) : null}
        </div>
    )
}

export default SwapPage
